import { atom } from 'jotai';

import { RoleStatus } from '../_components/_shared/types';

import { settingsRouteParamAtom } from './settingsModule.state';

/**
 * Role ID from route params
 */
export const $roleId = settingsRouteParamAtom(
  [
    '/team-and-permissions/roles/:roleId',
    '/team-and-permissions/roles/:roleId/permissions',
    '/team-and-permissions/roles/:roleId/users',
  ],
  'roleId',
);

/**
 * Tab state for role details view
 */
export type RoleDetailsTab = 'permissions' | 'users';

export const $roleDetailsTab = atom<RoleDetailsTab>('permissions');

/**
 * Current role being viewed/edited
 */
export interface CurrentRoleData {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  status: RoleStatus;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const $currentRole = atom<CurrentRoleData | null>(null);

/**
 * Role permissions state for editing
 * Maps permission key to enabled/disabled state
 */
export const $rolePermissions = atom<Record<string, boolean>>({});

/**
 * Original permissions snapshot for dirty checking
 */
export const $originalRolePermissions = atom<Record<string, boolean>>({});

/**
 * Update a single permission
 */
export const $updateRolePermission = atom(
  null,
  (get, set, update: { key: string; enabled: boolean }) => {
    const currentState = get($rolePermissions);
    set($rolePermissions, {
      ...currentState,
      [update.key]: update.enabled,
    });
  },
);

/**
 * Bulk update permissions
 */
export const $bulkUpdateRolePermissions = atom(
  null,
  (get, set, updates: Record<string, boolean>) => {
    const currentState = get($rolePermissions);
    set($rolePermissions, {
      ...currentState,
      ...updates,
    });
  },
);

/**
 * Derived atom to check if the form has unsaved changes
 */
export const $isRoleFormDirty = atom((get) => {
  const current = get($rolePermissions);
  const original = get($originalRolePermissions);

  const currentKeys = Object.keys(current);
  const originalKeys = Object.keys(original);

  if (currentKeys.length !== originalKeys.length) return true;

  return currentKeys.some((key) => current[key] !== original[key]);
});

/**
 * Initialize permissions from current role data
 */
export const $initRolePermissions = atom(null, (get, set) => {
  const currentRole = get($currentRole);
  if (currentRole) {
    const permissionState: Record<string, boolean> = {};
    currentRole.permissions.forEach((key) => {
      permissionState[key] = true;
    });
    set($rolePermissions, permissionState);
    set($originalRolePermissions, permissionState);
  }
});

/**
 * Reset permissions to original state (discard changes)
 */
export const $resetRolePermissions = atom(null, (get, set) => {
  const original = get($originalRolePermissions);
  set($rolePermissions, { ...original });
});

/**
 * Get enabled permission keys as array (for saving)
 */
export const $enabledPermissionKeys = atom((get) => {
  const permissions = get($rolePermissions);
  return Object.entries(permissions)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);
});

// =============================================================================
// Role Users Tab State
// =============================================================================

/**
 * Selected user IDs for bulk actions in role users tab
 */
export const $selectedRoleUserIds = atom<string[]>([]);

/**
 * Pagination state for role users tab
 */
export const $roleUsersOffset = atom<number>(0);
export const $roleUsersLimit = atom<number>(20);

/**
 * Filter state for role users tab
 */
export const $roleUsersFacilityFilter = atom<string[]>([]);
export const $roleUsersRolesFilter = atom<string[]>([]);

/**
 * Reset role users filters when changing roles
 */
export const $resetRoleUsersFilters = atom(null, (get, set) => {
  set($roleUsersOffset, 0);
  set($roleUsersFacilityFilter, []);
  set($roleUsersRolesFilter, []);
  set($selectedRoleUserIds, []);
});
